import type { ChatMessage } from './AiChatContext';

/**
 * Heuristic: user is asking for customer-facing copy (draft email, reply, etc.).
 * Used to append My Escapes link when we have a specific order in context.
 */
export function isCustomerFacingDraftQuery(text: string | null | undefined): boolean {
  if (!text?.trim()) {
    return false;
  }
  const t = text.toLowerCase();
  const internalOnlyChecks = [
    /\bbug report\b/,
    /\bescalat(e|ion|ing)\b/,
    /\binternal\b/,
    /\bjira\b/,
    /\bincident\b/,
    /\bslack\b/,
  ];
  if (internalOnlyChecks.some((re) => re.test(t))) {
    return false;
  }
  const checks = [
    /\bdraft\b/,
    /\bemail\b/,
    /\breply\b/,
    /\brespond\b/,
    /\bresponse\b.*\bcustomer\b/,
    /\bcustomer[-\s]?facing\b/,
    /\bwrite\b[\s\S]{0,40}\bcustomer\b/,
    /\bwrite\b[\s\S]{0,40}\bguest\b/,
    /\bsuggest\b[\s\S]{0,40}\b(reply|response|email|wording)\b/,
    /\bmessage\b[\s\S]{0,40}\bcustomer\b/,
    /\bmessage\b[\s\S]{0,40}\bguest\b/,
    /\bcompose\b[\s\S]{0,30}\bemail\b/,
    /\bsend\b[\s\S]{0,30}\bcustomer\b/,
    /\bdraft\b[\s\S]{0,80}\bguest\b/,
    /\bguest\b[\s\S]{0,80}\bdraft\b/,
  ];
  return checks.some((re) => re.test(t));
}

export function isDepartmentSelectionQuestion(text: string | null | undefined): boolean {
  if (!text?.trim()) {
    return false;
  }

  const t = text.toLowerCase();
  const mentionsDepartment = t.includes('department');
  const asksForChoice =
    t.includes('which department are you in') ||
    t.includes('what department are you in') ||
    t.includes('please choose your department') ||
    t.includes('choose one only') ||
    t.includes('choose one') ||
    t.includes('select one');
  const includesKnownOptions =
    t.includes('sales') &&
    t.includes('customer support') &&
    t.includes('tours') &&
    t.includes('cruises') &&
    t.includes('flight support');

  return (mentionsDepartment && asksForChoice) || (mentionsDepartment && includesKnownOptions);
}

export function extractDepartmentSelection(text: string | null | undefined, options: Array<string>): string | null {
  if (!text?.trim()) {
    return null;
  }

  const normalized = text.trim().replace(/\.$/, '').toLowerCase();

  for (const option of options) {
    const optionNormalized = option.toLowerCase();
    if (normalized === optionNormalized) {
      return option;
    }
    if (
      normalized === `my department is ${optionNormalized}` ||
      normalized === `i am in ${optionNormalized}` ||
      normalized === `i'm in ${optionNormalized}`
    ) {
      return option;
    }
  }

  return null;
}

export function getDepartmentForAssistantMessage(
  messages: Array<ChatMessage>,
  assistantIndex: number,
  options: Array<string>,
  conversationDepartment?: string | null,
): string | null {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    if (messages[i].role !== 'user') {
      continue;
    }

    const selectedDepartment = extractDepartmentSelection(messages[i].content, options);
    if (selectedDepartment) {
      return selectedDepartment;
    }
  }

  return conversationDepartment || null;
}

export function getPrecedingUserMessageContent(messages: Array<ChatMessage>, assistantIndex: number): string | null {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      return messages[i].content;
    }
  }
  return null;
}

export function buildMyEscapesOrderUrl(customerPortalBase: string | null | undefined, orderId: string): string | null {
  if (!customerPortalBase || !orderId?.trim()) {
    return null;
  }
  const base = customerPortalBase.replace(/\/$/, '');
  return `${base}/account/my-escapes/order/${encodeURIComponent(orderId)}/`;
}

/** Avoid duplicating if the model already included this booking link. */
export function messageAlreadyContainsMyEscapesOrderLink(content: string, orderId: string): boolean {
  if (!content || !orderId) {
    return false;
  }
  const encoded = encodeURIComponent(orderId);
  return (
    content.includes(`/account/my-escapes/order/${orderId}/`) ||
    content.includes(`/account/my-escapes/order/${encoded}/`)
  );
}

export function buildAssistantCopyText(assistantContent: string, myEscapesAppendix: string | null): string {
  if (!myEscapesAppendix) {
    return assistantContent;
  }
  return `${assistantContent.trimEnd()}\n\n${myEscapesAppendix}`;
}
